import React from "react";
import {
  Badge, Box, ButtonBase, IconButton, Paper, Stack, Typography
} from "@mui/material";
import {HugeiconsIcon} from "@hugeicons/react";
import {Cancel01Icon, Clock01Icon, DialpadCircle01Icon, MinusSignIcon, Settings02Icon, UserGroupIcon} from "@hugeicons/core-free-icons";
import {useNavigate} from "react-router-dom";
import {useRegistryProject} from "../../pages/registry-project/RegistryProjectContext";
import {ActiveCallCard, CallTimer, isCallLive} from "./ActiveCallCard";
import {useWebrtc} from "../../context/WebrtcContext";
import {PhoneCallsTab} from "./PhoneCallsTab";
import {PhoneDialTab} from "./PhoneDialTab";
import {PhoneQueueTab} from "./PhoneQueueTab";

// what the agent is doing right now, in order of importance: on a call, calls waiting, connection state
const statePillOf = (status, call, queue) => {
  if (isCallLive(call)) return {label: <>On a call · <CallTimer startedAt={call.startedAt}/></>, color: "#1E8A55", bg: "rgba(47, 194, 117, 0.22)"};
  if (queue.count > 0) return {label: `${queue.count} waiting`, color: "#A86A0C", bg: "rgba(245, 158, 11, 0.16)"};
  if (status === "registered") return {label: "Connected", color: "#1E8A55", bg: "rgba(47, 194, 117, 0.12)"};
  return {label: "Reconnecting…", color: "#A86A0C", bg: "rgba(245, 158, 11, 0.16)"};
};

const headerIconSx = {width: 32, height: 32, color: "#6B6B80", bgcolor: "#FAFAFD", "&:hover": {bgcolor: "#ECECF3", color: "#4B55C9"}};

// bottom navigation: the open tab grows to show its name, the others stay icons only
const NavItem = ({selected, label, icon, onClick}) => (
  <ButtonBase
    onClick={onClick} aria-label={label} aria-current={selected ? "page" : undefined}
    sx={{
      flex: selected ? 2.4 : 1, minWidth: 0, height: 44, gap: 1, borderRadius: "16px", overflow: "hidden", whiteSpace: "nowrap",
      color: selected ? "#4B55C9" : "#8A8AA0", bgcolor: selected ? "rgba(132, 145, 247, 0.14)" : "transparent",
      transition: "flex 0.4s cubic-bezier(0.22, 1, 0.36, 1), background-color 0.2s ease, color 0.2s ease",
      "&:hover": {bgcolor: selected ? "rgba(132, 145, 247, 0.14)" : "#ECECF3"},
    }}
  >
    {icon}
    {selected && <Typography component={"span"} sx={{fontSize: 13, fontWeight: 600}}>{label}</Typography>}
  </ButtonBase>
);

// The browser phone, laid out like a phone app: calls, dial pad and queue behind a bottom navigation. Full screen on
// phones, bottom right otherwise. tab: calls | dial | queue. queue: see useAgentQueue.
export const PhonePanel = ({tab, onChangeTab, onClose, queue, settings}) => {
  const {basePath} = useRegistryProject();
  const navigate = useNavigate();
  const {status, call} = useWebrtc();
  const pill = statePillOf(status, call, queue);

  const openConversation = (conversationId) => {
    onClose();
    navigate(`${basePath}/supervision?conversation=${encodeURIComponent(conversationId)}`);
  };

  return (
    <Paper elevation={0} role={"dialog"} aria-label={"Phone"} sx={{
      position: "fixed", zIndex: 1200, display: "flex", flexDirection: "column", overflow: "hidden", bgcolor: "#fff",
      inset: {xs: 0, md: "auto 28px 28px auto"},
      width: {xs: "100%", md: 360},
      height: {xs: "100%", md: "min(700px, calc(100vh - 72px))"},
      // on a desktop it is a phone: tall and narrow, rounded, with a soft light bezel instead of a border
      borderRadius: {xs: 0, md: "40px"},
      boxShadow: {xs: "none", md: "0 0 0 7px #F4F4FA, 0 24px 64px rgba(35, 38, 77, 0.22)"},
    }}>
      <Box aria-hidden sx={{
        display: {xs: "none", md: "block"}, alignSelf: "center", flexShrink: 0, mt: 1.25, width: 72, height: 6,
        borderRadius: "999px", bgcolor: "#ECECF3",
      }}/>
      <Stack direction={"row"} alignItems={"center"} justifyContent={"space-between"} sx={{
        px: 2.5, pt: {xs: 1.5, md: 1.25}, pb: 1, flexShrink: 0,
      }}>
        <Stack direction={"row"} alignItems={"center"} gap={1} sx={{minWidth: 0}}>
          <Typography sx={{fontSize: 16, fontWeight: 700, color: "#23264D"}}>
            {{calls: "Calls", dial: "Dial", queue: "Queue", settings: "Settings"}[tab]}
          </Typography>
          <Box sx={{
            px: 1.1, height: 24, display: "flex", alignItems: "center", borderRadius: "999px", fontSize: 12, fontWeight: 600,
            whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums", color: pill.color, bgcolor: pill.bg,
          }}>
            {pill.label}
          </Box>
        </Stack>
        <Stack direction={"row"} gap={0.5}>
          {isCallLive(call) && (
            <IconButton aria-label={"Minimize the phone"} size={"small"} onClick={onClose} sx={headerIconSx}>
              <HugeiconsIcon icon={MinusSignIcon} size={18}/>
            </IconButton>
          )}
          <IconButton aria-label={"Close the phone"} size={"small"} onClick={onClose} sx={headerIconSx}>
            <HugeiconsIcon icon={Cancel01Icon} size={18}/>
          </IconButton>
        </Stack>
      </Stack>

      <ActiveCallCard onOpened={onClose}/>

      <Box sx={{flex: 1, minHeight: 0, display: "flex", flexDirection: "column"}}>
        {tab === "calls" && <PhoneCallsTab onOpenConversation={openConversation}/>}
        {tab === "dial" && <PhoneDialTab/>}
        {tab === "queue" && <PhoneQueueTab queue={queue} onOpenConversation={openConversation}/>}
        {tab === "settings" && <Box sx={{flex: 1, minHeight: 0, overflowY: "auto"}}>{settings}</Box>}
      </Box>

      <Stack direction={"row"} alignItems={"center"} gap={0.5} role={"navigation"} aria-label={"Phone"} sx={{
        flexShrink: 0, height: 56, mx: 1.5, mb: {xs: 1.5, md: 0.75}, p: 0.75, borderRadius: "22px", bgcolor: "#FAFAFD",
      }}>
        <NavItem label={"Calls"} selected={tab === "calls"} onClick={() => onChangeTab("calls")}
                 icon={<HugeiconsIcon icon={Clock01Icon} size={22}/>}/>
        <NavItem label={"Dial"} selected={tab === "dial"} onClick={() => onChangeTab("dial")}
                 icon={<HugeiconsIcon icon={DialpadCircle01Icon} size={22}/>}/>
        <NavItem label={"Queue"} selected={tab === "queue"} onClick={() => onChangeTab("queue")} icon={
          <Badge badgeContent={queue.count} max={99} sx={{"& .MuiBadge-badge": {color: "#fff", bgcolor: "#A86A0C"}}}>
            <HugeiconsIcon icon={UserGroupIcon} size={22}/>
          </Badge>
        }/>
        <NavItem label={"Settings"} selected={tab === "settings"} onClick={() => onChangeTab("settings")}
                 icon={<HugeiconsIcon icon={Settings02Icon} size={22}/>}/>
      </Stack>
      <Box aria-hidden sx={{
        display: {xs: "none", md: "block"}, alignSelf: "center", flexShrink: 0, mb: 1, width: 112, height: 4,
        borderRadius: "999px", bgcolor: "#E1E1EC",
      }}/>
    </Paper>
  );
};
